import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DeleteForever as RemoveIcon,
  GpsFixed as GpsIcon,
  LinkOff as UnlinkIcon,
  RestoreFromTrash as RestoreIcon,
} from "@mui/icons-material";
import {
  useGetDeviceByTrailerQuery,
  useGetAvailableDevicesQuery,
  useLinkDeviceToTrailerMutation,
  useUnlinkDeviceMutation,
  useRemoveDeviceMutation,
  useRestoreDeviceMutation,
  DeviceSummary,
} from "../../../api/anyTrackApi";

interface TrailerDeviceTabProps {
  trailerId: number;
}

const STATUS_COLORS: Record<string, string> = {
  Online: "#4CAF50",
  Offline: "#757575",
  Unplugged: "#f44336",
  Removed: "#9E9E9E",
};

const TrailerDeviceTab: React.FC<TrailerDeviceTabProps> = ({ trailerId }) => {
  const { data: linkedDevice, isLoading, refetch } = useGetDeviceByTrailerQuery(trailerId);
  const { data: availableDevices = [], isLoading: loadingAvailable } = useGetAvailableDevicesQuery(
    undefined,
    { skip: !!linkedDevice }
  );

  const [linkDevice, { isLoading: isLinking }] = useLinkDeviceToTrailerMutation();
  const [unlinkDevice, { isLoading: isUnlinking }] = useUnlinkDeviceMutation();
  const [removeDevice, { isLoading: isRemoving }] = useRemoveDeviceMutation();
  const [restoreDevice, { isLoading: isRestoring }] = useRestoreDeviceMutation();

  const [selectedDevice, setSelectedDevice] = React.useState<DeviceSummary | null>(null);

  const handleLink = async () => {
    if (!selectedDevice) return;
    await linkDevice({ deviceId: selectedDevice.deviceId, trailerId });
    setSelectedDevice(null);
    refetch();
  };

  const handleUnlink = async () => {
    if (!linkedDevice) return;
    await unlinkDevice(linkedDevice.deviceId);
    refetch();
  };

  const handleRemove = async () => {
    if (!linkedDevice) return;
    await removeDevice({ deviceId: linkedDevice.deviceId });
    refetch();
  };

  const handleRestore = async () => {
    if (!linkedDevice) return;
    await restoreDevice(linkedDevice.deviceId);
    refetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const statusColor = linkedDevice ? STATUS_COLORS[linkedDevice.currentStatus] ?? "#757575" : undefined;
  const isUnplugged = linkedDevice?.currentStatus === "Unplugged";
  const isRemoved = linkedDevice?.isRemoved;
  const isBusy = isLinking || isUnlinking || isRemoving || isRestoring;

  return (
    <Stack spacing={3}>
      <Typography variant="subtitle1" fontWeight={600}>
        GPS Tracking Device
      </Typography>

      {linkedDevice ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            {/* Device info header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <GpsIcon sx={{ color: statusColor, fontSize: "1.5rem" }} />
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {linkedDevice.vehicleName || linkedDevice.deviceId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    IMEI: {linkedDevice.deviceId}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={linkedDevice.currentStatus}
                size="small"
                sx={{
                  bgcolor: `${statusColor}20`,
                  color: statusColor,
                  fontWeight: 600,
                  borderColor: `${statusColor}50`,
                  border: 1,
                }}
              />
            </Box>

            {/* Device details */}
            <Divider />
            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {linkedDevice.licensePlateNumber && (
                <Box>
                  <Typography variant="caption" color="text.secondary">License Plate</Typography>
                  <Typography variant="body2">{linkedDevice.licensePlateNumber}</Typography>
                </Box>
              )}
              {linkedDevice.lastBatteryLevel != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Battery</Typography>
                  <Typography variant="body2">{linkedDevice.lastBatteryLevel}%</Typography>
                </Box>
              )}
              {linkedDevice.lastExternalVoltage != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">External Power</Typography>
                  <Typography variant="body2">
                    {linkedDevice.lastExternalVoltage > 0 ? "Connected" : "Disconnected"}
                  </Typography>
                </Box>
              )}
              {linkedDevice.lastLatitude != null && linkedDevice.lastLongitude != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Last Position</Typography>
                  <Typography variant="body2">
                    {linkedDevice.lastLatitude.toFixed(4)}, {linkedDevice.lastLongitude.toFixed(4)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Unplug alert */}
            {isUnplugged && (
              <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                This device was unplugged{linkedDevice.unplugDetectedAt
                  ? ` on ${new Date(linkedDevice.unplugDetectedAt).toLocaleString()}`
                  : ""}. The last known location is shown on the map.
              </Alert>
            )}

            {/* Actions */}
            <Divider />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {!isRemoved && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<RemoveIcon />}
                  onClick={handleRemove}
                  disabled={isBusy}
                >
                  Remove Device
                </Button>
              )}

              {isRemoved && (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<RestoreIcon />}
                  onClick={handleRestore}
                  disabled={isBusy}
                >
                  Restore Device
                </Button>
              )}

              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<UnlinkIcon />}
                onClick={handleUnlink}
                disabled={isBusy}
              >
                Unlink Device
              </Button>
            </Box>
          </Stack>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              No GPS device is linked to this trailer. Select a device below to start tracking.
            </Typography>

            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Autocomplete
                options={availableDevices}
                getOptionLabel={(opt) => opt.vehicleName || opt.deviceId}
                renderOption={(props, option) => (
                  <li {...props} key={option.deviceId}>
                    <Box>
                      <Typography variant="body2">{option.vehicleName || option.deviceId}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        IMEI: {option.deviceId}
                        {option.licensePlateNumber ? ` | ${option.licensePlateNumber}` : ""}
                      </Typography>
                    </Box>
                  </li>
                )}
                value={selectedDevice}
                onChange={(_, value) => setSelectedDevice(value)}
                loading={loadingAvailable}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select GPS Device"
                    placeholder="Search by name or IMEI..."
                    size="small"
                  />
                )}
                noOptionsText="No available devices"
              />
              <Button
                variant="contained"
                onClick={handleLink}
                disabled={!selectedDevice || isBusy}
                sx={{ minWidth: 100, height: 40 }}
              >
                {isLinking ? <CircularProgress size={20} /> : "Link"}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default TrailerDeviceTab;
